import type { Copy } from "../types";

export const vi: Copy = {
    seo: {
      title: "Máy tính tuổi xe | kiểm tra tuổi xe theo năm và tháng",
      description:
        "Kiểm tra tuổi chính xác của xe từ năm sản xuất và tháng tùy chọn. Xem năm, tháng, ngày, tổng số ngày và các mốc quan trọng trong một công cụ gọn gàng.",
      keywords: [
        "máy tính tuổi xe",
        "máy tính tuổi phương tiện",
        "kiểm tra năm sản xuất xe",
        "kiểm tra tháng sản xuất xe",
        "xe của tôi bao nhiêu tuổi",
        "kiểm tra tuổi xe theo năm",
        "kiểm tra tuổi xe",
        "máy tính tuổi RC",
        "kiểm tra tuổi xe theo tháng",
        "máy tính ngày xe",
        "mParivahan thay thế",
        "Parivahan thay thế",
        "máy tính tuổi xe RTO",
        "kiểm tra tuổi đăng ký xe",
        "tuổi đăng ký phương tiện",
      ],
      faq: [
        {
          question: "Máy tính này làm gì?",
          answer:
            "Công cụ này tính tuổi chính xác của xe từ năm sản xuất đã chọn và tháng tùy chọn, sau đó hiển thị năm, tháng, ngày và thông tin mốc quan trọng.",
        },
        {
          question: "Vì sao có tháng tùy chọn?",
          answer:
            "Thêm tháng sẽ giúp tính tuổi chính xác hơn. Nếu không chọn tháng, hệ thống sẽ dùng tháng 1 của năm đã chọn.",
        },
        {
          question: "Tôi có thể chọn năm hoặc tháng trong tương lai không?",
          answer:
            "Không. Năm trong tương lai bị chặn và tháng trong tương lai bị vô hiệu hóa khi năm đã chọn là năm hiện tại.",
        },
      ],
    },
    headerTagline: "Tiện ích xe",
    headerTitle: "Máy tính tuổi xe",
    reset: "Đặt lại",
    switchToLight: "Chuyển sang chế độ sáng",
    switchToDark: "Chuyển sang chế độ tối",
    languageLabel: "Ngôn ngữ",
    languageEnglish: "Tiếng Anh",
    languageHindi: "Tiếng Hindi",
    heroEyebrow: "Tuổi xe, rõ ràng",
    heroTitle: "Một công cụ ngày tháng gọn gàng để xem tuổi xe.",
    heroDescription:
      "Chọn năm sản xuất. Chỉ thêm tháng nếu bạn muốn kết quả chính xác hơn.",
    yearSelectLabel: "Năm sản xuất",
    monthSelectLabel: "Tháng sản xuất",
    includeMonthLabel: "Bao gồm tháng cụ thể để chính xác hơn",
    includeMonthNote: "Nếu không bao gồm tháng, hệ thống sẽ dùng tháng 1 để tính toán.",
    yearOnlyBadge: "Chỉ năm",
    yearMonthBadge: "Năm + tháng",
    yearPlaceholder: "Chọn năm",
    monthPlaceholder: "Chọn tháng",
    inputTitle: "Năm sản xuất",
    inputLabel: "Năm sản xuất",
    blockedDateNote: "Không thể chọn năm trong tương lai.",
    result: "Kết quả",
    vehicleAgeLead: "Xe của bạn",
    asOfToday: "Tính đến hôm nay",
    years: "Năm",
    months: "Tháng",
    days: "Ngày",
    totalDays: (count) => `Tổng ${count.toLocaleString("en-IN")} ngày`,
    ageLabels: { almostNew: "Gần như mới", modern: "Hiện đại", mature: "Trưởng thành", classic: "Cổ điển" },
    estimatedValue: {
      high: "Tiềm năng bán lại cao",
      healthy: "Giữ giá tốt",
      moderate: "Khấu hao vừa phải",
      collector: "Có thể tăng sức hút với người sưu tầm",
    },
    maintenanceTitle: "Bảo dưỡng",
    maintenance: {
      routine: "Bảo dưỡng định kỳ và chăm sóc OEM",
      watch: "Theo dõi phanh, dầu và lốp",
      plan: "Lên kế hoạch cho linh kiện hao mòn và kiểm tra",
      specialized: "Bảo dưỡng chuyên sâu trở nên quan trọng",
    },
    registrationTitle: "Đăng ký",
    registration: {
      collector: "Một số khu vực có thể áp dụng chế độ xe sưu tầm",
      classic: "Một số khu vực có thể áp dụng đăng ký xe cổ",
      standard: "Hãy kỳ vọng gia hạn tiêu chuẩn và kiểm tra khu vực",
      regular: "Thông thường áp dụng đăng ký thông thường",
    },
    upcomingMilestoneTitle: "Mốc tiếp theo",
    milestoneTimelineTitle: "Dòng thời gian mốc quan trọng",
    milestoneRemaining: ({ label, days }) => `${label} sau ${days.toLocaleString("vi-VN")} ngày`,
    reached: "Đã đạt",
    upcoming: "Sắp tới",
    allMilestonesReachedTitle: "Tất cả các mốc hiển thị đã đạt",
    allMilestonesReachedBody: "Xe này đã vượt qua phạm vi mốc hiện được hiển thị trong ứng dụng.",
    copySummary: "Sao chép tóm tắt",
    shareResult: "Chia sẻ kết quả",
    resetMessage: "Đã đặt lại máy tính",
    summaryCopied: "Đã sao chép tóm tắt vào clipboard",
    clipboardFailed: "Không truy cập được clipboard",
    emptyTitle: "Chọn năm sản xuất để xem đầy đủ chi tiết",
    emptyDescription:
      "Khi chọn năm, bạn sẽ thấy tuổi chính xác, tổng số ngày, ghi chú bảo dưỡng, trạng thái cổ điển và thời điểm mốc quan trọng.",
    shareTitle: "Máy tính tuổi xe",
    summary: ({ label, years, months, days, asOf }) =>
      `Xe của tôi được sản xuất vào ${label} và tính đến ${asOf} đã ${years} năm ${months} tháng ${days} ngày.`,
    milestones: {
      1: { label: "Năm đầu tiên", note: "Hữu ích cho bảo hành và kế hoạch bảo dưỡng." },
      3: { label: "Ba năm", note: "Mốc thường gặp cho bán lại và thay đổi giá trị." },
      5: { label: "Năm năm", note: "Thời điểm tốt để xem lại ngân sách bảo dưỡng." },
      10: { label: "Mười năm", note: "Các bộ phận cũ cần được chú ý nhiều hơn." },
      15: { label: "Mười lăm năm", note: "Nhiều khu vực bắt đầu xử lý theo kiểu xe cổ." },
      25: { label: "Hai mươi lăm năm", note: "Có thể hấp dẫn nhà sưu tầm và nhập khẩu ở một số thị trường." },
    },
  };
